import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadFile, getPresignedUrl } from '../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { resolveViewerMimeType } from './constants';

interface DocIdViewerState {
    docId: string;
    fileName: string;
    fileUrl: string;
    mimeType?: string;
}

export interface DocIdFileViewerProps {
    heading: string;
    fileUrl: string;
    mimeType?: string;
    fileName?: string;
    downloading: boolean;
    onDownload: () => void;
    onClose: () => void;
}

export interface UseDocIdFileViewerResult {
    open: (docId: string, fileName: string) => Promise<void>;
    close: () => void;
    /** Null while no file is open — the caller renders `<FileViewerDrawer {...viewerProps} />`
     *  only when this is non-null. */
    viewerProps: DocIdFileViewerProps | null;
}

/**
 * Opens a docId in the shared FileViewerDrawer with presigned-URL + mime resolution.
 * `getPresignedUrl` swallows its own error and returns `undefined`, so the `!res?.uri` check
 * below is what surfaces a toast instead of a blank drawer.
 */
export function useDocIdFileViewer(): UseDocIdFileViewerResult {
    const [viewerState, setViewerState] = useState<DocIdViewerState | null>(null);
    const [downloading, setDownloading] = useState(false);
    const inFlightRef = useRef(false);

    const open = async (docId: string, fileName: string) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const res = await getPresignedUrl({ docId: Number(docId) }, false);
            if (!res?.uri) {
                toast.error('Could not open this file. Please try again.');
                return;
            }
            setViewerState({
                docId,
                fileName,
                fileUrl: res.uri,
                mimeType: resolveViewerMimeType(fileName, res.fileType),
            });
        } catch (err) {
            console.error('Failed to open source file:', err);
            toast.error('Could not open this file. Please try again.');
        } finally {
            inFlightRef.current = false;
        }
    };

    const close = () => setViewerState(null);

    const handleDownload = () => {
        if (!viewerState) return;
        setDownloading(true);
        downloadFile(viewerState.docId, () => {}, viewerState.fileName)
            .catch(() => {})
            .finally(() => setDownloading(false));
    };

    const viewerProps: DocIdFileViewerProps | null = viewerState
        ? {
              heading: viewerState.fileName,
              fileUrl: viewerState.fileUrl,
              mimeType: viewerState.mimeType,
              fileName: viewerState.fileName,
              downloading,
              onDownload: handleDownload,
              onClose: close,
          }
        : null;

    return { open, close, viewerProps };
}
